import React, { useState, useRef, useEffect } from "react";
import { 
  Calculator, Target, GraduationCap, Users, Globe, Clock, AlertCircle, 
  BookOpen, TrendingUp, ChevronRight 
} from "lucide-react";
import { motion } from "framer-motion";
import ContactForm from "../components/ContactForm";
import { Link } from "react-router-dom";

const PTEPage = () => {
  const [calculator, setCalculator] = useState({
    listening: 50, reading: 50, speaking: 50, writing: 50
  });
  const [overall, setOverall] = useState(50);
  const [cefr, setCefr] = useState("B1");

  const updateScore = () => {
    const total = Object.values(calculator).reduce((a, b) => a + b, 0) / 4;
    setOverall(Math.round(total));
    const cefrMap = {
      30: "A2", 43: "B1", 58: "B2", 65: "C1", 79: "C1", 89: "C2"
    };
    const level = Object.keys(cefrMap).reverse().find(key => total >= key) || "A2";
    setCefr(level);
  };

  useEffect(() => {
    updateScore();
  }, [calculator]);

  const selfSelectCards = [
    { icon: GraduationCap, title: "University Applicants", desc: "Seeking admission to institutions in UK, Australia, Canada, and New Zealand." },
    { icon: Users, title: "Professionals", desc: "Requiring certification for skilled migration or career advancement." },
    { icon: TrendingUp, title: "Time-Sensitive Candidates", desc: "Needing results within 48 hours for application deadlines." }
  ];

  const benchmarks = [
    { score: "58+", meaning: "Minimum for many student visas (UK, Australia)", unis: "Regional Australian unis, select UK institutions" },
    { score: "65+", meaning: "Typical undergraduate entry requirement", unis: "University of Melbourne, Toronto Metropolitan University, many Canadian unis" },
    { score: "79+", meaning: "Competitive for postgraduate programs", unis: "Select Russell Group unis, top-tier North American programs (institution-specific)" }
  ];

  const myths = [
    { text: "Good conversational English guarantees high scores", counter: "PTE tests academic proficiency via integrated tasks" },
    { text: "Memorized templates ensure 79+ automatically", counter: "AI detects patterns; fluency/content scored" },
    { text: "Skipping easy questions doesn't impact overall band", counter: "All responses contribute to partial credit" },
    { text: "PTE is easier than IELTS for all skill levels", counter: "Format suits some; depends on strengths" }
  ];

  const testFormat = [
    { section: "Speaking & Writing", duration: "54-67 minutes" },
    { section: "Reading", duration: "29-30 minutes" },
    { section: "Listening", duration: "30-43 minutes" }
  ];

  const preparationFlow = [
    { step: "Diagnostic Assessment", desc: "Identify baseline band score and skill gaps." },
    { step: "Personalized Roadmap", desc: "Weekly targets based on target score and timeline." },
    { step: "Expert Feedback", desc: "Detailed analysis of pronunciation, fluency, and content." },
    { step: "Mock Tests", desc: "Timed simulations with score reports mirroring official format." }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* 1. Hero + Calculator */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-light text-slate-900 leading-tight">
              PTE Academic Overview
            </h1>
            <p className="text-xl text-slate-600 max-w-lg leading-relaxed">
              Computer-based English proficiency test accepted by over 3,500 universities and governments worldwide for study, work, and migration visas.
            </p>
            <p className="text-lg text-slate-500">
              Designed for academic and professional English assessment with results typically available within 48 hours.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="w-6 h-6 text-emerald-600" />
              <h3 className="text-xl font-semibold text-slate-900">Estimate Your Overall Band</h3>
            </div>
            <p className="text-xs text-slate-500 mb-6 italic">
              This is an estimate based on equal weighting. Actual PTE scores use Pearson's algorithm with partial credit.
            </p>
            <div className="space-y-4">
              {["listening", "reading", "speaking", "writing"].map(section => (
                <div key={section} className="space-y-1">
                  <label className="text-sm font-medium text-slate-700 capitalize">
                    {section.replace(/^\w/, c => c.toUpperCase())}
                  </label>
                  <input
                    type="range"
                    min="10" max="90"
                    value={calculator[section]}
                    onChange={e => setCalculator({...calculator, [section]: +e.target.value})}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-600"
                  />
                  <span className="text-sm font-mono text-slate-600">{calculator[section]}</span>
                </div>
              ))}
              <div className="pt-6 border-t border-slate-200">
                <div className="text-3xl font-bold text-slate-900 mb-1">Overall: {overall}</div>
                <div className="flex items-center gap-2 text-emerald-700">
                  <span className="font-semibold">{cefr}</span>
                  <span className="text-sm">(CEFR Level)</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Who Should Take PTE */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Who Takes PTE Academic?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Primary candidate profiles for academic and migration pathways.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {selfSelectCards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 border border-slate-100 hover:border-emerald-200"
            >
              <card.icon className="w-12 h-12 text-emerald-600 mb-6 mx-auto" />
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">{card.title}</h3>
              <p className="text-slate-600 leading-relaxed">{card.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3. CTA Cluster (Position 3) */}
      <section className="py-24 px-6 md:px-12 bg-emerald-50 border-t border-emerald-200">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-2xl shadow-lg">
            <BookOpen className="w-8 h-8 text-emerald-600" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Next Steps</h3>
              <p className="text-sm text-slate-600">Review preparation pathways</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/test-prep#pte">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl flex items-center gap-2"
            >
              PTE Preparation Programs
              <ChevronRight className="w-5 h-5" />
            </motion.button>
            </Link>
            <Link to="/test-prep">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="border-2 border-slate-600 hover:border-slate-400 bg-white text-slate-900 px-8 py-4 rounded-2xl text-lg font-semibold flex items-center gap-2"
            >
              All Test Prep
            </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Score Benchmarks */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            What Your Score Means
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {benchmarks.map((bench, i) => (
            <motion.div
              key={bench.score}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 hover:shadow-xl border border-slate-100"
            >
              <div className="text-3xl font-bold text-emerald-600 mb-4">{bench.score}</div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">{bench.meaning}</h4>
              <p className="text-slate-600">{bench.unis}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5. Global Acceptance */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Global Acceptance
          </h2>
          <p className="text-xl text-slate-600">
            Accepted for study visas and skilled migration by governments in UK, Australia, Canada, New Zealand.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-3xl p-10">
            <Globe className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">Study Abroad</h3>
            <p className="text-lg text-slate-600">University admissions and visa applications.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-10">
            <Target className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">Migration Visas</h3>
            <p className="text-lg text-slate-600">Skilled migration programs and permanent residency pathways.</p>
          </motion.div>
        </div>
      </section>

      {/* 6. Common Misconceptions */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-12 text-center">
            Common Misconceptions
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {myths.map((myth, i) => (
              <motion.div
                key={myth.text}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="flex flex-col gap-4 p-6 bg-white rounded-2xl border border-slate-200 hover:border-amber-200 group"
              >
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
                  <p className="text-slate-700 group-hover:text-slate-900 font-medium">{myth.text}</p>
                </div>
                <p className="text-sm text-slate-600 font-semibold bg-amber-50 p-3 rounded-xl">
                  {myth.counter}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Test Structure */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Test Structure
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-16">
            Total: 2-3 hours. Integrated tasks across sections.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {testFormat.map((format, i) => (
            <motion.div
              key={format.section}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 text-center border border-slate-100 hover:shadow-lg"
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-4 capitalize">{format.section}</h3>
              <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <div className="text-2xl font-bold text-slate-900">{format.duration}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 8. How We Help You Prepare */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            How We Help You Prepare
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Structured preparation aligned with PTE's AI-enabled scoring criteria.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {preparationFlow.map((step, i) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, x: i % 2 ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-10 border border-slate-100 hover:shadow-xl hover:border-emerald-200"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-emerald-700">{i + 1}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{step.step}</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 9. ContactForm (LAST) */}
      <section id="contact-form" className="max-w-6xl mx-auto px-8 pb-12 bg-slate-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Questions About PTE?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Schedule a consultation for score pathway advice.
          </p>
        </div>
        <ContactForm />
      </section>
    </div>
  );
};

export default PTEPage;